import {
  useFetchListOrderReady,
  useFetchListInprogress,
  useFetchListOnHold,
  useFetchListFinished,
  useFetchListByStatusSlug,
  useFetchListRTO,
  useFetchOrderEnRoute,
  useFetchOrderCollecting,
  useFetchReturningOrigin,
  useFetchReturningWarehouse,
  useFetchActionRequiredOrderList,
  useFetchListRtoRequest,
} from '@/hooksApi/useOrder';
import { OrderStatusTab } from '@/types/columns.ts';
import { OrderListResponse, Product } from '@/types';
import { RtoNote } from '@/types/order';
import { formatDateByDayjs, getOrderParams, getOrderPickupAddress } from '@/utils';
import useSearchStore, { ISearch } from './useSearchStore';
import { UseQueryResult } from '@tanstack/react-query';
import { MerchantTitleOrderStatusEnum, OrderStatusEnum } from '@/config';
import { OrderFilters } from '@/stores/useFilterOrderStore';
import { extractAppliedRule } from '@/utils/extractAppliedRule';
import { OrderSla } from '@/types/sla';
import { orderReasonText } from '@/utils/orderReason';

export interface OrderStatus {
  slug: OrderStatusEnum;
  merchantTitle: MerchantTitleOrderStatusEnum;
}
export interface OrderDataType {
  key: string;
  orderDate: string;
  orderDateRaw: string;
  // FE-2: formatted date when this order was first marked for return.
  // null for non-RTO orders.
  rtoRequestedAt?: string | null;
  // BOXY-376 — the merchant's return note (RTO lists only).
  rtoNote?: RtoNote | null;
  orderId: string;
  customerName?: string;
  customerAddress?: string;
  orderValue: number;
  platform_code: string;
  custom_id: string;
  status: OrderStatus;
  // True when the order was collected via a BULK pickup. Used to render
  // "Bulk Collected" instead of "Collected" while status.slug === 'collected'.
  is_bulk_collected?: boolean;
  // TEMP-9: cold-chain flag — renders the Snowflake pill in the list.
  temperature_control?: boolean;
  // BOXY-301 R14: box count — renders the multi-box pill. ABSENT on a
  // single-box order rather than 1; the pill keys off that absence.
  box_count?: number;
  // One-way "a label was generated" flag — drives the Reprint swap + Printed pill.
  label_printed?: boolean;
  // BOXY-378 — first partial delivery; drives the list pill after the order moves on.
  partiallyDeliveredAt?: string | null;
  pickupLocation: string;
  pickupAddressId: string | null;
  products: Product[];
  driver: {
    phone: string | undefined;
    assign: boolean;
  };
  merchant_action_required: number;
  merchant_action_required_text: string;
  carrier_message: string;
  // SLA state from the automations service. ALWAYS present, OFTEN null -
  // null means "no SLA information", which renders as a blank cell.
  sla?: OrderSla | null;
  phone: string;
  // Item 2 — rule attribution from the merchant order payload.
  // `applied_rule_uid` / `applied_rule_name` identify the ShippingRule that
  // ACTUALLY assigned the order's carrier. Both are null when the merchant
  // overrode the carrier (no rule attribution) — the UI then shows nothing.
  applied_rule_uid?: string | null;
  applied_rule_name?: string | null;
}

type FetchDataFunction = (
  page: number,
  pageSize?: number,
  enabled?: boolean,
  search?: ISearch,
  filterParams?: URLSearchParams,
) => UseQueryResult<OrderListResponse, Error>;

const useOrderData = (
  current: number,
  activeTab: OrderStatusTab,
  activeButton: string,
  pageSize: number,
  orderFilters: OrderFilters,
) => {
  const { searchValue: searchTermHeader, setSearchValue: setSearchTermHeader } = useSearchStore();

  const apiCallEnabled =
    //  orderFilters.searchValue?.searchBy
    //   ? !!orderFilters.searchValue?.search
    //   :

    true;

  //define API call fo each tab
  const useFetchListData: {
    [key in OrderStatusTab]: Record<string, FetchDataFunction>;
  } = {
    [OrderStatusTab.READY_TO_SHIP]: { ['all']: useFetchListOrderReady },
    [OrderStatusTab.IN_PROGRESS]: {
      ['all']: useFetchListInprogress,
      ['en-route']: useFetchOrderEnRoute,
      ['collecting']: useFetchOrderCollecting,
    },
    [OrderStatusTab.ON_HOLD]: {
      ['all']: useFetchListOnHold,
      ['action-required']: useFetchActionRequiredOrderList,
    },
    [OrderStatusTab.RTO]: {
      ['all']: useFetchListRTO,
      ['rto-requested']: useFetchListRtoRequest,
      ['rto-in-transit']: useFetchReturningOrigin,
      ['rto-received-warehouse']: useFetchReturningWarehouse,
    },
    [OrderStatusTab.INVENTORY]: { ['all']: useFetchListFinished },
    [OrderStatusTab.FINISHED]: { ['all']: useFetchListFinished },
  };

  const filterParams = getOrderParams(orderFilters);

  // get order list using status slug(used for some of tabs)
  const fetchWithStatusSlug = (
    page: number,
    pageSize?: number,
    enabled?: boolean,
    search?: ISearch,
  ) => {
    return useFetchListByStatusSlug(activeButton, page, pageSize, search, filterParams);
  };

  // if selectedTab has it own API use it. else=> use fetchWithStatusSlug
  const queryFunction = useFetchListData[activeTab][activeButton] || fetchWithStatusSlug;
  const { data, isLoading, isFetching, refetch } = queryFunction(
    current,
    pageSize,
    apiCallEnabled,
    searchTermHeader,
    // orderFilters.searchValue,
    filterParams,
  );

  // transform data
  const mapOrderData = (data: OrderListResponse | undefined): OrderDataType[] => {
    if (!data?.object) return [];
    return (
      data?.object?.items?.map((item) => ({
        key: item.uid,
        orderDate: formatDateByDayjs(item.created_at, 'D MMMM YYYY'),
        orderDateRaw: item.created_at,
        // FE-2: surface the "Return requested" timestamp on the RTO tab. Accepts
        // either the appended `rto_requested_at` string from /orders/rto (list
        // endpoint) or the same field from /orders/search (Meilisearch path).
        rtoRequestedAt: (() => {
          const flat = (item as any)?.rto_requested_at;
          if (flat) return formatDateByDayjs(flat, 'D MMMM YYYY');
          const rtoStatus = (item as any)?.statuses?.find(
            (s: any) => s?.slug === 'rto-requested' || s?.slug === 'rto-request-pending',
          );
          const ts = rtoStatus?.pivot?.created_at;
          return ts ? formatDateByDayjs(ts, 'D MMMM YYYY') : null;
        })(),
        // BOXY-376 — the return note, on the RTO lists.
        rtoNote: item?.rto_note ?? null,
        orderId: item?.platform_code,
        customerName: item?.full_name,
        customerAddress: item?.address_text,
        orderValue: item?.products_value,
        platform_code: item?.platform_code,
        custom_id: item?.custom_id || '---',
        status: {
          slug: (item?.status?.slug as OrderStatusEnum) || '',
          merchantTitle: (item?.status?.merchant_title as MerchantTitleOrderStatusEnum) ?? '',
        },
        is_bulk_collected: item?.is_bulk_collected ?? false,
        // TEMP-9: thread the cold-chain flag so the list can badge the order.
        temperature_control: item?.temperature_control ?? false,
        /*
         * BOXY-301 R14 — the box count, and it belongs in BOTH mappers.
         *
         * I added it to useOrderListData only, which is the same half-fix
         * TEMP-9's own comment records: a trait carried by one of the two
         * mappers drops its pill on whichever path uses the other, and nothing
         * fails. Passed through undefined rather than defaulted to 0 or 1 — the
         * backend OMITS the key for a single-box order and the pill keys off
         * that absence.
         */
        box_count: item?.box_count,
        label_printed: item?.label_printed ?? false,
        partiallyDeliveredAt: item?.partially_delivered_at ?? null,
        // ! We need slug to realize the exact status of the order (not merchant_title) for checking "inProgressActionsByStatus".
        carrier_message: item?.carrier_message || '---',
        // Passed through unchanged so the column owns every rendering decision.
        sla: (item as any)?.sla ?? null,
        pickupLocation: getOrderPickupAddress(item)?.title || '---',
        pickupAddressId: getOrderPickupAddress(item)?.uid || '',
        products: item?.products?.map((product) => ({
          name: product?.title,
          quantity: product?.quantity,
          price: product?.price,
        })),
        driver: {
          phone: item?.driver?.phone,
          assign: Boolean(item?.driver) ?? false,
        },
        merchant_action_required: item?.merchant_action_required || 0,
        merchant_action_required_text: orderReasonText(item),
        phone: item.phone,
        carrier: item?.carrier,
        // The service level the order shipped on. In Boxy Select the carrier
        // column is not built at all, so without this the list has nothing to
        // say about how the order ships.
        service_code: item?.service_code ?? null,
        ...extractAppliedRule(item as unknown as Record<string, unknown>),
      })) || []
    );
  };

  const totals = isFetching || isLoading ? 0 : data?.object.total || 0;

  const activeData = isFetching || isLoading ? [] : mapOrderData(data);

  return {
    activeData,
    totals,
    isLoading,
    isFetching,
    refetch,
  };
};

export default useOrderData;
